import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatRelativeTime } from '../lib/utils';
import {
  Search,
  Loader2,
  ArrowLeft,
  Download,
  Star,
  Crown,
  Sparkles,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Scroll,
  Users,
  MapPin,
  Map,
  Swords,
  Compass,
  Calendar,
  BookMarked,
} from 'lucide-react';

const CATEGORIES = [
  { value: 'campaign', key: 'marketplace_category_campaign' },
  { value: 'one-shot', key: 'marketplace_category_oneshot' },
  { value: 'module', key: 'marketplace_category_module' },
  { value: 'adventure', key: 'marketplace_category_adventure' },
];

const DIFFICULTIES = [
  { value: 'beginner', key: 'marketplace_difficulty_beginner' },
  { value: 'intermediate', key: 'marketplace_difficulty_intermediate' },
  { value: 'advanced', key: 'marketplace_difficulty_advanced' },
  { value: 'expert', key: 'marketplace_difficulty_expert' },
];

const CONTENT_ICONS = {
  sessions: Calendar,
  quests: Compass,
  npcs: Users,
  locations: MapPin,
  maps: Map,
  encounters: Swords,
  lore_articles: BookMarked,
};

function getDifficultyColor(difficulty) {
  switch (difficulty) {
    case 'beginner': return 'bg-success/20 text-success';
    case 'intermediate': return 'bg-info/20 text-info';
    case 'advanced': return 'bg-warning/20 text-warning';
    case 'expert': return 'bg-danger/20 text-danger';
    default: return 'bg-muted text-muted-foreground';
  }
}

function getCategoryColor(category) {
  switch (category) {
    case 'campaign': return 'bg-primary/20 text-primary';
    case 'one-shot': return 'bg-accent/20 text-accent';
    case 'module': return 'bg-magic/20 text-magic';
    case 'adventure': return 'bg-gold/20 text-gold';
    default: return 'bg-muted text-muted-foreground';
  }
}

const TemplateCard = ({ template, language, onClick }) => {
  const contentKeys = Object.entries(template.included_content || {}).filter(([, v]) => v > 0);
  const totalContent = contentKeys.reduce((sum, [, v]) => sum + v, 0);

  return (
    <Card
      className="glass-card card-interactive card-lift cursor-pointer group overflow-hidden"
      onClick={onClick}
    >
      {/* Cover Image */}
      <div className="relative h-44 bg-gradient-to-br from-primary/20 via-accent/10 to-magic/20 overflow-hidden">
        {template.cover_image_url ? (
          <img
            src={template.cover_image_url}
            alt={template.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Scroll className="h-12 w-12 text-primary/30" />
          </div>
        )}
        {/* Badges overlay */}
        <div className="absolute top-2 right-2 flex gap-1">
          {template.language && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-bold uppercase">
              {template.language}
            </Badge>
          )}
          {template.is_official && (
            <Badge className="bg-primary text-primary-foreground text-xs gap-1">
              <Crown className="h-3 w-3" />
              {t('marketplace_official', language)}
            </Badge>
          )}
          {template.is_featured && (
            <Badge className="bg-accent text-accent-foreground text-xs gap-1">
              <Star className="h-3 w-3" />
            </Badge>
          )}
        </div>
        {/* Category pill */}
        <div className="absolute bottom-2 left-2">
          <span className={`status-chip ${getCategoryColor(template.category)}`}>
            {t(`marketplace_category_${template.category}`, language)}
          </span>
        </div>
      </div>

      <CardHeader className="pb-2">
        <CardTitle className="font-cinzel text-base leading-tight line-clamp-1">
          {template.name}
        </CardTitle>
        <CardDescription className="text-xs">
          {template.universe}
          {template.game_system && ` · ${template.game_system}`}
        </CardDescription>
      </CardHeader>

      <CardContent className="pt-0 space-y-3">
        <p className="text-sm text-muted-foreground line-clamp-2">
          {template.short_description || template.long_description || '—'}
        </p>

        {/* Tags + Difficulty */}
        <div className="flex flex-wrap gap-1">
          <span className={`status-chip text-[10px] ${getDifficultyColor(template.difficulty)}`}>
            {t(`marketplace_difficulty_${template.difficulty}`, language)}
          </span>
          {(template.tags || []).slice(0, 3).map(tag => (
            <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0">
              {tag}
            </Badge>
          ))}
        </div>

        {/* Content summary */}
        {totalContent > 0 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {contentKeys.slice(0, 4).map(([key, count]) => {
              const Icon = CONTENT_ICONS[key];
              return Icon ? (
                <span key={key} className="flex items-center gap-0.5">
                  <Icon className="h-3 w-3" />{count}
                </span>
              ) : null;
            })}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-border/50">
          <span>{t('marketplace_by', language)} {template.author_name}</span>
          <span className="flex items-center gap-1">
            <Download className="h-3 w-3" />
            {template.import_count || 0}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

const SkeletonCard = () => (
  <Card className="glass-card overflow-hidden">
    <div className="h-44 bg-muted animate-pulse" />
    <CardHeader className="pb-2">
      <div className="h-5 bg-muted animate-pulse rounded w-3/4" />
      <div className="h-3 bg-muted animate-pulse rounded w-1/2 mt-2" />
    </CardHeader>
    <CardContent className="pt-0 space-y-3">
      <div className="h-4 bg-muted animate-pulse rounded" />
      <div className="h-4 bg-muted animate-pulse rounded w-2/3" />
      <div className="flex gap-1">
        <div className="h-5 bg-muted animate-pulse rounded-full w-16" />
        <div className="h-5 bg-muted animate-pulse rounded-full w-12" />
      </div>
    </CardContent>
  </Card>
);

export default function MarketplacePage() {
  const { language } = useAuth();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [category, setCategory] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [sortBy, setSortBy] = useState('published_at');

  const LIMIT = 12;

  const fetchFeatured = useCallback(async () => {
    setLoadingFeatured(true);
    try {
      const res = await axios.get('/api/marketplace/featured');
      setFeatured(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingFeatured(false);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * LIMIT,
        limit: LIMIT,
        sort_by: sortBy,
        sort_order: sortBy === 'name' ? 1 : -1,
      };
      if (search) params.search = search;
      if (category) params.category = category;
      if (difficulty) params.difficulty = difficulty;

      const res = await axios.get('/api/marketplace/templates', { params });
      setTemplates(res.data.templates);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, category, difficulty, sortBy]);

  useEffect(() => {
    fetchFeatured();
  }, [fetchFeatured]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(0);
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <MainLayout showSidebar={false}>
      <div className="space-y-8 animate-fade-in">
        {/* Back + Title */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/campaigns')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-cinzel text-3xl font-bold">{t('marketplace', language)}</h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              {t('marketplace_hero_subtitle', language)}
            </p>
          </div>
        </div>

        {/* Hero Section with Featured */}
        {featured.length > 0 && !search && !category && !difficulty && page === 0 && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-accent" />
              <h2 className="font-cinzel text-xl font-semibold">{t('marketplace_featured', language)}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {loadingFeatured
                ? Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
                : featured.map(tmpl => (
                    <TemplateCard
                      key={tmpl.id}
                      template={tmpl}
                      language={language}
                      onClick={() => navigate(`/marketplace/${tmpl.id}`)}
                    />
                  ))
              }
            </div>
          </section>
        )}

        {/* Filters Bar */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="h-5 w-5 text-primary" />
            <h2 className="font-cinzel text-xl font-semibold">{t('marketplace_all_templates', language)}</h2>
            {total > 0 && (
              <Badge variant="secondary" className="ml-2">{total}</Badge>
            )}
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder={t('marketplace_search_placeholder', language)}
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                />
              </div>
              <Button type="submit" variant="secondary">
                <Search className="h-4 w-4" />
              </Button>
            </form>

            {/* Category filter */}
            <Select value={category} onValueChange={v => { setCategory(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder={t('marketplace_all_categories', language)} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('marketplace_all_categories', language)}</SelectItem>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{t(c.key, language)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Difficulty filter */}
            <Select value={difficulty} onValueChange={v => { setDifficulty(v === 'all' ? '' : v); setPage(0); }}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder={t('marketplace_all_difficulties', language)} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('marketplace_all_difficulties', language)}</SelectItem>
                {DIFFICULTIES.map(d => (
                  <SelectItem key={d.value} value={d.value}>{t(d.key, language)}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={v => { setSortBy(v); setPage(0); }}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="published_at">{t('marketplace_sort_recent', language)}</SelectItem>
                <SelectItem value="import_count">{t('marketplace_sort_popular', language)}</SelectItem>
                <SelectItem value="name">{t('marketplace_sort_name', language)}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </section>

        {/* Templates Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : templates.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <BookOpen className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <h3 className="font-cinzel text-lg font-semibold mb-1">{t('marketplace_no_results', language)}</h3>
              <p className="text-muted-foreground text-sm">{t('marketplace_no_results_desc', language)}</p>
              {(search || category || difficulty) && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => { setSearch(''); setSearchInput(''); setCategory(''); setDifficulty(''); setPage(0); }}
                >
                  {language === 'fr' ? 'Réinitialiser les filtres' : 'Reset filters'}
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {templates.map(tmpl => (
              <TemplateCard
                key={tmpl.id}
                template={tmpl}
                language={language}
                onClick={() => navigate(`/marketplace/${tmpl.id}`)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-4">
            <Button
              variant="outline"
              size="icon"
              disabled={page === 0}
              onClick={() => setPage(p => Math.max(0, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground px-3">
              {page + 1} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
